import _clonedeep from 'lodash.clonedeep';
import {
  authGet,
  authPost,
  authDelete,
  // orderStatuses,
  updateNestedState,
  packageCollectionTypes,
  makeuuid,
  findPhysicalItemIdentifierKey,
  includesPhysicalItemIdentifier,
  normalizePhysicalItemIdentifier,
  physicalItemIdentifiersEqual,
  hasPhysicalItemTrackingValues,
} from '../../lib';
import {
  setSelectedOrder,
  resetOrderFields,
  // setCanOnlyShip,
  setPhysicalItems,
  setInventoryPool,
  showError,
  showNotification,
  setPackages,
  setItemsRemaining,
  setItemsCompleted,
  setPackageEditId,
  setHasUnsealedPackage,
  setIsReship,
  updateReshipProperties,
  setUILoading,
  setShouldPromptDocQty,
  setHasUnsealedPallet,
  setPalletId,
  setWorkstationId,
  setScannedOrderItemMapping,
  setFulfillmentScheduleCache,
  setFulfillmentProgressCache,
  setFulfillmentExpressShipmentsCache,
  setFulfillmentOpenOrdersPerCustomerCache,
  setSkusAllowingSerialOnOutbound,
  bulkSetOrderProcessingState,
} from '../features';
import { addResource, customPost } from './resource';
import { AppThunk, NormalizeReturn } from '../types';
import { isBefore, addMinutes, parseISO } from 'date-fns';
import {
  ExpressProcessingWorkflowType,
  GetScannedOrderItemDataRequest,
  InventoryPool,
  MoveablePalletForExpressProcessingBasicResponse,
  MoveablePalletOrderItemsBasicResponse,
  InventoryRequirements,
  MoveablePalletOrdersBasicResponse,
  OrderDetailResponse,
  OrderFulfillmentScheduleRequest,
  OrderItemAPI,
  OrderItemScanResponse,
  OrderPhysicalItemAPI,
  OrdersHeaderData,
  PackageCollectionType,
  PhysicalItemCombination,
  PrintOrderPackageLabelRequest,
  PrintOrderPackageLabelResponse,
  ProcessOrderAPI,
  ProcessReshipReturnAPI,
  RemainingPhysicalItemsRequiredType,
  RecordConsumedInventoryParams,
  RemoveConsumedInventoryParams,
} from '../../types';

export function markOrderReady(
  orderId: number | string,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch) => {
    dispatch(setUILoading(true));
    const { data, error } = await authPost(`orders/${orderId}/mark-ready`, {});
    dispatch(setUILoading(false));
    if (error) {
      dispatch(
        showError({
          message:
            error.message ||
            error.title ||
            'Failed to mark order as ready to process',
        }),
      );
      return { error };
    }
    dispatch(
      showNotification({ message: 'Order marked as ready to process.' }),
    );
    return { data };
  };
}

export function getOrderById(
  orderId: number | string,
): AppThunk<Promise<NormalizeReturn<OrderDetailResponse>>> {
  return async (dispatch) => {
    const { data, error } = await authGet(`orders/${orderId}`);
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    return { data };
  };
}

export function ordersPageHeaders(): AppThunk<
  Promise<NormalizeReturn<OrdersHeaderData>>
> {
  return async (dispatch) => {
    const { data, error } = await authGet('/orders/list-header');
    if (error) {
      return { error };
    }
    return { data };
  };
}

function getProcessOrder({
  orderNumber,
  isInternalOrderId,
  shouldResetFields = true,
  shouldShowError,
  isReship,
  palletId,
  workstationId,
}: {
  orderNumber: number | string;
  isInternalOrderId?: boolean;
  shouldResetFields?: boolean;
  shouldShowError?: boolean;
  isReship?: boolean;
  palletId?: number;
  workstationId?: number;
}): AppThunk<Promise<NormalizeReturn<ProcessOrderAPI>>> {
  return async (dispatch) => {
    shouldResetFields && dispatch(resetOrderFields());
    const { data, error } = await authGet<ProcessOrderAPI>([
      `orders/process/${orderNumber}`,
      {
        isInternalOrder: isInternalOrderId,
        getAllStatuses: !!isReship,
        palletID: palletId,
        workstationID: workstationId,
      },
    ]);
    if (error) {
      shouldShowError && dispatch(showError({ message: error.message }));
      return { error };
    }
    // The order was processed but not shipped.
    // const isNotShippedOrder =
    //   data.status === orderStatuses.CLOSED && !data.hasShipped;
    shouldResetFields && dispatch(setSelectedOrder(data));
    isReship && dispatch(setIsReship(true));
    palletId && dispatch(setPalletId(palletId));
    workstationId && dispatch(setWorkstationId(workstationId));
    dispatch(getShouldPromptDocQty(isReship ?? false));
    // shouldResetFields &&
    //   isNotShippedOrder &&
    //   dispatch(setCanOnlyShip(isNotShippedOrder));
    return { data };
  };
}

function getShouldPromptDocQty(isReship: boolean) {
  return (dispatch, getState) => {
    const {
      orders: { selectedOrder: { documents } = {} },
    } = getState();
    const shouldPrompt =
      !isReship &&
      Array.isArray(documents) &&
      documents.some(
        (d) =>
          d.documentFeeType === 'CartonLabelDocumentFee' ||
          d.documentFeeType === 'ItemLabelDocumentFee',
      );
    dispatch(setShouldPromptDocQty(shouldPrompt));
  };
}

function updateItemsRemaining({
  sku,
  value,
  itemProperty,
  currentRemainingState,
}: {
  sku: string;
  value: string;
  itemProperty: string;
  currentRemainingState?: Record<string, any>;
}) {
  return (dispatch, getState) => {
    const {
      orders: { itemsRemaining = {}, originalItems = {} },
    } = getState();
    let itemsRemainingState = _clonedeep(itemsRemaining);
    // if the item was removed from the itemsRemaining list adn they change the quantity we need to add it back
    if (!itemsRemaining[sku]) {
      itemsRemainingState = {
        ...itemsRemainingState,
        [sku]: originalItems[sku],
      };
    }
    const path = [sku, ...ensurePropertyArray(itemProperty)];
    const newState = updateNestedState({
      nestedKey: path,
      value,
      state: currentRemainingState || itemsRemainingState,
    });
    dispatch(setItemsRemaining(newState));
    return newState;
  };
}

function updateItemsCompleted({
  sku,
  value,
  itemProperty,
  currentCompletedState,
}: {
  sku: string;
  value: string;
  itemProperty: string;
  currentCompletedState?: Record<string, any>;
}) {
  return (dispatch, getState) => {
    const {
      orders: { itemsCompleted = {} },
    } = getState();
    const path = [sku, ...ensurePropertyArray(itemProperty)];
    const newState = updateNestedState({
      nestedKey: path,
      value,
      state: currentCompletedState || itemsCompleted,
    });
    dispatch(setItemsCompleted(newState));
    return newState;
  };
}

function updatePackages({
  packageNum,
  childPackageNumber,
  sku,
  value,
  itemProperty,
  collection,
  currentPackagesState,
  shouldOverrideValue,
  shouldDispatch = true,
}: {
  packageNum: number;
  childPackageNumber?: number;
  sku?: string;
  value: Record<string, any>;
  itemProperty: string;
  collection: PackageCollectionType;
  currentPackagesState?: Record<string, any>;
  shouldOverrideValue?: boolean;
  shouldDispatch?: boolean;
}) {
  return (dispatch, getState) => {
    const {
      orders: { packages = {} },
    } = getState();

    const path = [
      packageNum,
      ...(childPackageNumber ? ['childPackages', childPackageNumber] : []),
      ...ensurePropertyArray(collection), // if updating more then one collection leave empty and specify with payload
      ...ensurePropertyArray(sku), // for material there is no sku
      ...ensurePropertyArray(itemProperty),
    ];
    const newState = updateNestedState({
      nestedKey: path,
      value,
      state: currentPackagesState || packages,
      shouldOverrideValue,
    });
    if (shouldDispatch) {
      dispatch(setPackages(newState));
    }
    return newState;
  };
}

function syncItemsLists({
  sku,
  originalPackageItemQty,
  currentItemsRemainingState,
  currentItemsCompletedState,
  shouldDispatchChanges = true,
}: {
  sku: string;
  originalPackageItemQty: number;
  currentItemsRemainingState?: Record<string, any>;
  currentItemsCompletedState?: Record<string, any>;
  shouldDispatchChanges?: boolean;
}) {
  return (dispatch, getState) => {
    const {
      orders: { itemsRemaining = {}, itemsCompleted = {}, originalItems = {} },
    } = getState();
    let itemsRemainingCurrentState =
      currentItemsRemainingState || itemsRemaining;
    let itemsCompletedCurrentState =
      currentItemsCompletedState || itemsCompleted;
    const originalItemCompletedQty =
      originalItems[sku].quantity - originalPackageItemQty;
    const newState = updateNestedState({
      nestedKey: [sku, 'quantity'],
      value: originalPackageItemQty,
      state: itemsRemainingCurrentState,
    });
    itemsRemainingCurrentState = newState;
    if (originalItemCompletedQty === 0) {
      // remove item from itemsCompleted
      const { [sku]: itemCompletedToDelete, ...otherCompletedItems } =
        itemsCompletedCurrentState;
      itemsCompletedCurrentState = otherCompletedItems;
    } else {
      const newCompletedState = updateNestedState({
        nestedKey: [sku, 'quantity'],
        value: originalItems[sku].quantity - originalPackageItemQty,
        state: itemsCompletedCurrentState,
      });
      itemsCompletedCurrentState = newCompletedState;
    }
    shouldDispatchChanges &&
      dispatch(setItemsRemaining(itemsRemainingCurrentState));
    shouldDispatchChanges &&
      dispatch(setItemsCompleted(itemsCompletedCurrentState));
    return { itemsRemainingCurrentState, itemsCompletedCurrentState };
  };
}

function removePackedAsFromItemRemaining({
  itemRemainingState,
  itemPackedPerUnitOfMeasureCount,
}: {
  itemRemainingState: Record<string, any>;
  itemPackedPerUnitOfMeasureCount: Record<string, any>;
}) {
  if (
    !itemPackedPerUnitOfMeasureCount ||
    !itemRemainingState?.packedPerUnitOfMeasureCount
  )
    return itemRemainingState;

  const updatedPackedAsState = Object.keys(
    itemPackedPerUnitOfMeasureCount,
  ).reduce((acc, uom) => {
    const qtyForUom = itemRemainingState.packedPerUnitOfMeasureCount[uom];
    if (qtyForUom) {
      const qtyForUomForPackage = itemPackedPerUnitOfMeasureCount[uom];
      acc[uom] = qtyForUom - qtyForUomForPackage;
    }
    return acc;
  }, {});

  return {
    ...itemRemainingState,
    packedPerUnitOfMeasureCount: {
      ...itemRemainingState.packedPerUnitOfMeasureCount,
      ...updatedPackedAsState,
    },
  };
}

function deletePackage({
  packageNum,
  childPackageNumber,
  originalQtyItemsInPackage,
  updatedPackage,
}: {
  packageNum: string;
  childPackageNumber?: number;
  originalQtyItemsInPackage: Record<string, number>;
  updatedPackage?: Record<string, any>;
}) {
  return (dispatch, getState) => {
    const {
      orders: {
        selectedOrder = {},
        itemsRemaining = {},
        itemsCompleted = {},
        originalItems = {},
        packages = {},
        packageEditId,
      },
    } = getState();
    let currentPackage = updatedPackage || packages[packageNum];

    if (childPackageNumber) {
      currentPackage = currentPackage?.childPackages?.[childPackageNumber];
    }

    if (!currentPackage) return;
    if (packageEditId) {
      dispatch(setPackageEditId(null));
    }

    // Legacy pallets can contain parent items as well as cartons. Keep the
    // original groups for tracking release, including split quantities of one lot.
    const packageItemGroups = [
      currentPackage,
      ...Object.values(currentPackage.childPackages || {}),
    ].map((pkg: any) => pkg.items || {});
    const packageItems = getGroupedPackageItems(packageItemGroups);

    // reset itemsRemaining and itemsCompleted
    if (packageItems) {
      let itemsRemainingState = itemsRemaining;
      let itemsCompletedState = itemsCompleted;
      const packagePhysicalItemsGroup = {};

      Object.keys(packageItems).forEach((sku) => {
        if (!itemsRemaining[sku]) {
          itemsRemainingState = {
            ...itemsRemainingState,
            [sku]: { ...originalItems[sku], quantity: 0 },
          };
        } else {
          itemsRemainingState = {
            ...itemsRemainingState,
            [sku]: removePackedAsFromItemRemaining({
              itemRemainingState: itemsRemaining[sku],
              itemPackedPerUnitOfMeasureCount:
                packageItems[sku].packedPerUnitOfMeasureCount,
            }),
          };
        }

        const { itemsCompletedCurrentState, itemsRemainingCurrentState } =
          dispatch(
            syncItemsLists({
              sku,
              originalPackageItemQty: originalQtyItemsInPackage[sku],
              currentItemsRemainingState: itemsRemainingState,
              currentItemsCompletedState: itemsCompletedState,
              shouldDispatchChanges: false, // only dispatch once
            }),
          );
        itemsRemainingState = itemsRemainingCurrentState;
        itemsCompletedState = itemsCompletedCurrentState;

        const itemPhysicalItems = packageItemGroups.flatMap((items) =>
          Object.values(items[sku]?.physicalItems || {}),
        );
        if (itemPhysicalItems.length)
          packagePhysicalItemsGroup[sku] = itemPhysicalItems;
      });

      dispatch(setItemsRemaining(itemsRemainingState));
      dispatch(setItemsCompleted(itemsCompletedState));

      if (Object.keys(packagePhysicalItemsGroup).length) {
        // For Extensiv orders: return physical items to the legacy pool
        if (selectedOrder.orderCreationType === 'Extensiv') {
          dispatch(
            addMultipleItemsToPhysicalItemsState(packagePhysicalItemsGroup),
          );
        } else {
          // For non-Extensiv orders: update inventory pool for each SKU
          Object.entries(packagePhysicalItemsGroup).forEach(
            ([sku, physicalItems]) => {
              const piArray = physicalItems as any[];
              const items = piArray.map((pi) => ({
                lotNumber: pi.lotNumber,
                expirationDate: pi.expirationDate,
                serialNumber: pi.serialNumber,
                quantity: pi.quantity ?? 1,
              }));
              dispatch(
                removeConsumedInventory({
                  sku,
                  items,
                  packageId: packageNum,
                  childPackageNumber,
                  currentPackagesState: packages,
                  clearPackagePhysicalItems: false,
                }),
              );
            },
          );
        }
      }
    }

    let newPackageState = {};
    if (childPackageNumber) {
      const { [childPackageNumber]: packageToDelete, ...otherChildPackages } =
        packages[packageNum].childPackages;
      const isEmptyPallet =
        !Object.keys(otherChildPackages).length &&
        !Object.keys(packages[packageNum].items || {}).length;

      newPackageState = {
        ...packages,
        [packageNum]: {
          ...packages[packageNum],
          childPackages: otherChildPackages,
          // Removing the last carton leaves a draft pallet, with its ID and
          // material intact, that must be packed again before sealing.
          ...(isEmptyPallet
            ? {
                metadata: {
                  ...packages[packageNum].metadata,
                  isCompleted: false,
                },
              }
            : {}),
        },
      };
      if (isEmptyPallet) {
        dispatch(setHasUnsealedPallet(true));
      }
    } else {
      const { [packageNum]: packageToDelete, ...otherPackages } = packages;
      newPackageState = otherPackages;
    }
    dispatch(setPackages(newPackageState));
  };
}

function getGroupedPackageItems(packageItemGroups: Record<string, any>[]) {
  return packageItemGroups.reduce((acc, items) => {
    if (items) {
      Object.keys(items).forEach((sku) => {
        const item = items[sku];
        const accumulatedItem = acc[sku];

        if (!accumulatedItem) {
          acc[sku] = item;
        } else {
          const {
            quantity: currentQuantity,
            packedPerUnitOfMeasureCount: currentUomCount = {},
          } = acc[sku];
          const { quantity, packedPerUnitOfMeasureCount = {} } = item;

          const summedUomCount = Array.from(
            new Set([
              ...Object.keys(currentUomCount),
              ...Object.keys(packedPerUnitOfMeasureCount),
            ]),
          ).reduce((a, c) => {
            const uomCount = currentUomCount[c] ?? 0;
            const itemUomCount = packedPerUnitOfMeasureCount[c] ?? 0;
            a[c] = itemUomCount + uomCount;
            return a;
          }, {});

          acc[sku] = {
            ...accumulatedItem,
            quantity: currentQuantity + quantity,
            packedPerUnitOfMeasureCount: summedUomCount,
          };
        }
      });
    }
    return acc;
  }, {});
}

function removeItemFromPackage({
  packageNum,
  childPackageNumber,
  sku,
  originalPackageItemQty,
}: {
  packageNum: string;
  childPackageNumber?: number;
  sku: string;
  originalPackageItemQty: number;
}) {
  return (dispatch, getState) => {
    const {
      orders: { selectedOrder = {}, packages = {}, itemsRemaining = {} },
    } = getState();
    let currentPackage = packages[packageNum];

    if (childPackageNumber) {
      currentPackage = currentPackage?.childPackages?.[childPackageNumber];
    }

    if (!currentPackage?.items) return;
    // update package
    const { [sku]: itemToDelete, ...otherItems } = currentPackage.items;
    if (!Object.keys(otherItems).length) {
      dispatch(
        deletePackage({
          packageNum,
          childPackageNumber,
          originalQtyItemsInPackage: { [sku]: originalPackageItemQty },
        }),
      );
    } else {
      let newPackageState = {};

      if (childPackageNumber) {
        newPackageState = {
          ...packages,
          [packageNum]: {
            ...packages[packageNum],
            childPackages: {
              ...packages[packageNum].childPackages,
              [childPackageNumber]: {
                ...packages[packageNum].childPackages[childPackageNumber],
                items: otherItems,
              },
            },
          },
        };
      } else {
        newPackageState = {
          ...packages,
          [packageNum]: {
            ...packages[packageNum],
            items: otherItems,
          },
        };
      }

      dispatch(setPackages(newPackageState));
      // update items remaining
      const itemsRemainingState = {
        ...itemsRemaining,
        [sku]: removePackedAsFromItemRemaining({
          itemRemainingState: itemsRemaining[sku],
          itemPackedPerUnitOfMeasureCount:
            currentPackage.items[sku].packedPerUnitOfMeasureCount,
        }),
      };

      dispatch(
        syncItemsLists({
          sku,
          originalPackageItemQty,
          currentItemsRemainingState: itemsRemainingState,
        }),
      );
      // update global physical items collection
      const physicalItemsToReturn = itemToDelete?.physicalItems;
      if (physicalItemsToReturn) {
        const idsToRemove = Object.keys(physicalItemsToReturn);

        // For Extensiv orders: return physical items to the legacy pool
        if (selectedOrder.orderCreationType === 'Extensiv') {
          const itemsToRemove = selectedOrder.physicalItems.filter((p) =>
            idsToRemove.includes(p.id + ''),
          );
          dispatch(addToPhysicalItemsState(sku, itemsToRemove));
        } else {
          // For non-Extensiv orders: update inventory pool
          const items = Object.values(physicalItemsToReturn).map((pi: any) => ({
            lotNumber: pi.lotNumber,
            expirationDate: pi.expirationDate,
            serialNumber: pi.serialNumber,
            quantity: pi.quantity ?? 1,
          }));
          dispatch(
            removeConsumedInventory({
              sku,
              items,
              packageId: packageNum,
              childPackageNumber,
              currentPackagesState: newPackageState,
              clearPackagePhysicalItems: false,
            }),
          );
        }
      }
    }
  };
}

function removeItemsRemaining(skus: string[] = []) {
  return (dispatch, getState) => {
    const {
      orders: { itemsRemaining = {} },
    } = getState();
    let currentItemsRemainingState = itemsRemaining;
    skus.forEach((sku) => {
      const { [sku]: itemToDelete, ...otherItems } = currentItemsRemainingState;
      currentItemsRemainingState = otherItems;
    });
    dispatch(setItemsRemaining(currentItemsRemainingState));
  };
}

function removeMaterialItem({
  packageNum,
  childPackageNumber,
  sku,
}: {
  packageNum: string;
  childPackageNumber?: number;
  sku: string;
}) {
  return (dispatch, getState) => {
    const {
      orders: { packages = {} },
    } = getState();
    let currentMaterial = packages[packageNum]?.material;

    if (childPackageNumber) {
      currentMaterial =
        packages[packageNum]?.childPackages?.[childPackageNumber]?.material;
    }

    if (!currentMaterial) return;
    const { [sku]: itemToDelete, ...otherItems } = currentMaterial;
    let newPackageState = {};
    if (childPackageNumber) {
      newPackageState = {
        ...packages,
        [packageNum]: {
          ...packages[packageNum],
          childPackages: {
            ...packages[packageNum].childPackages,
            [childPackageNumber]: {
              ...packages[packageNum].childPackages[childPackageNumber],
              material: otherItems,
            },
          },
        },
      };
    } else {
      newPackageState = {
        ...packages,
        [packageNum]: {
          ...packages[packageNum],
          material: otherItems,
        },
      };
    }

    dispatch(setPackages(newPackageState));
  };
}

/**
 * Gets random physical items from the imported array
 * and removes it from that array
 */
function getAndUpdateItemPhysicalItems(sku: string, totalItems: number) {
  return (dispatch, getState) => {
    const {
      orders: { physicalItems = {} },
    } = getState();

    const physicalItemsForSku = physicalItems[sku];
    if (!Array.isArray(physicalItemsForSku)) return [];
    // TODO handle for this
    if (physicalItemsForSku.length < totalItems) return [];
    const cloned = _clonedeep(physicalItemsForSku);
    const itemsToReturn = cloned.splice(0, totalItems);
    const newState = {
      ...physicalItems,
      [sku]: cloned,
    };
    dispatch(setPhysicalItems(newState));

    return itemsToReturn;
  };
}

/**
 * This put's physical items back into the `physicalItems` state when removing it from track inventory
 */
function addToPhysicalItemsState(sku: string, physicalItemsToAdd = []) {
  return (dispatch, getState) => {
    const {
      orders: { physicalItems = {} },
    } = getState();
    const physicalItemsForSku = physicalItems[sku] || [];
    const updatedPhysicalItemsForSku = [
      ...physicalItemsForSku,
      ...physicalItemsToAdd,
    ];
    const newState = {
      ...physicalItems,
      [sku]: updatedPhysicalItemsForSku,
    };
    dispatch(setPhysicalItems(newState));
  };
}

/**
 * This put's physical items for multiple items back into the `physicalItems` collection (ie when deleting a package)
 */
function addMultipleItemsToPhysicalItemsState(
  physicalItemsGroup: Record<string, any>,
) {
  return (dispatch, getState) => {
    const {
      orders: { physicalItems = {} },
    } = getState();
    if (!physicalItemsGroup) return;

    const addedItems = Object.keys(physicalItemsGroup).reduce((acc, sku) => {
      const physicalItemsForSku = physicalItems[sku] || [];
      const updatedPhysicalItemsForSku = [
        ...physicalItemsForSku,
        ...physicalItemsGroup[sku],
      ];
      acc[sku] = updatedPhysicalItemsForSku;
      return acc;
    }, {});

    const newState = {
      ...physicalItems,
      ...addedItems,
    };
    dispatch(setPhysicalItems(newState));
  };
}

/**
 * Update the package item with the correct physical items when removing some
 */
function removePhysicalItemsFromPackage({
  packageId,
  childPackageNumber,
  sku,
  physicalItemIdsToRemove,
  currentPackagesState,
}: {
  packageId: number;
  childPackageNumber?: number;
  sku: string;
  physicalItemIdsToRemove: string[];
  currentPackagesState?: Record<string, any>;
}) {
  return (dispatch, getState) => {
    let allPackages = currentPackagesState;
    if (!currentPackagesState) {
      allPackages = getState().orders.packages || {};
    }
    let selectedPackage = allPackages?.[packageId];
    if (childPackageNumber) {
      selectedPackage = selectedPackage?.childPackages?.[childPackageNumber];
    }
    if (!selectedPackage) return;

    const currentItemPhysicalItems =
      selectedPackage.items?.[sku]?.physicalItems;
    if (!currentItemPhysicalItems) return;

    const updatedPhysicalItems = Object.keys(currentItemPhysicalItems).reduce(
      (acc, cur) => {
        if (!physicalItemIdsToRemove.includes(cur + '')) {
          acc[cur] = currentItemPhysicalItems[cur];
        }
        return acc;
      },
      {},
    );
    let newState = {};
    if (childPackageNumber) {
      newState = {
        ...allPackages,
        [packageId]: {
          ...selectedPackage,
          childPackages: {
            ...selectedPackage[packageId].childPackages,
            [childPackageNumber]: {
              ...allPackages?.[packageId].childPackages[childPackageNumber],
              items: {
                ...selectedPackage.items,
                [sku]: {
                  ...selectedPackage.items[sku],
                  physicalItems: updatedPhysicalItems,
                },
              },
            },
          },
        },
      };
    } else {
      newState = {
        ...allPackages,
        [packageId]: {
          ...selectedPackage,
          items: {
            ...selectedPackage.items,
            [sku]: {
              ...selectedPackage.items[sku],
              physicalItems: updatedPhysicalItems,
            },
          },
        },
      };
    }
    dispatch(setPackages(newState));
  };
}

/**
 * Adds physical items when updating qty manually and `not` with inventory tracking.
 */
function addPhysicalItems({
  packageId,
  childPackageNumber,
  currentPackagesState,
  sku,
  qty,
  newPhysicalItems: _newPhysicalItems,
}: {
  packageId: number;
  childPackageNumber?: number;
  currentPackagesState?: Record<string, any>;
  sku: string;
  qty: number;
  newPhysicalItems: Record<string, any>;
}) {
  return (dispatch, getState) => {
    // Get the required amount of physical items from the `physicalItems` array for this sku and remove them from the array
    // For now we are returning any random items. TODO handle for when editing closed orders
    const {
      orders: { physicalItems = {} },
    } = getState();

    const physicalItemsForSku = physicalItems[sku];

    // if not tracking inventory and there is no physical items for the item then there is no need to do anything
    if (!_newPhysicalItems && !physicalItemsForSku) return;

    const newPhysicalItems = _newPhysicalItems || {};

    if (!_newPhysicalItems) {
      let dbPhysicalItems = {};

      if (physicalItemsForSku?.length) {
        dbPhysicalItems = dispatch(getAndUpdateItemPhysicalItems(sku, qty));
      }

      for (let i = 0; i < qty; i++) {
        const dbPhysicalItem = dbPhysicalItems[i] || {
          expirationDate: null,
          lotNumber: '',
          serialNumber: '',
        };
        newPhysicalItems[dbPhysicalItem.id ?? makeuuid(6)] = dbPhysicalItem;
      }
    }
    dispatch(
      updatePackages({
        currentPackagesState: currentPackagesState,
        packageNum: packageId,
        childPackageNumber,
        collection: packageCollectionTypes.ITEMS,
        sku,
        itemProperty: 'physicalItems',
        value: newPhysicalItems,
      }),
    );
  };
}

/**
 * Adds physical items when doing inventory tracking. This is called only the first time and not when editing existing physical items
 */
function addPhysicalItemsWithInventoryTracking({
  packageId,
  childPackageNumber,
  currentPackagesState,
  sku,
  inventoryState,
}: {
  packageId: number;
  childPackageNumber?: number;
  currentPackagesState?: Record<string, any>;
  sku: string;
  inventoryState: Record<string, any>;
}) {
  return (dispatch) => {
    // Get the required amount of physical items from the `physicalItems` array for this sku and remove them from the array
    // For now we are returning any random items. TODO handle for when editing closed orders
    const inventoryStateKeys = Object.keys(inventoryState);
    const dbPhysicalItems = dispatch(
      getAndUpdateItemPhysicalItems(sku, inventoryStateKeys.length),
    );
    const value = inventoryStateKeys.reduce((acc, cur, index) => {
      const dbPhysicalItem = dbPhysicalItems[index] || {};
      const updatedInventory = inventoryState[cur];
      // There may not be a dbPhysicalItem. For example if no physicalItems were returned with the order
      acc[dbPhysicalItem.id ?? makeuuid(6)] = {
        ...dbPhysicalItem,
        ...updatedInventory,
        // A blank optional serial entry should not erase an imported serial.
        serialNumber:
          updatedInventory.serialNumber || dbPhysicalItem.serialNumber || '',
      };

      return acc;
    }, {});

    dispatch(
      addPhysicalItems({
        packageId,
        childPackageNumber,
        currentPackagesState,
        sku,
        qty: inventoryStateKeys.length,
        newPhysicalItems: value,
      }),
    );
  };
}

/**
 * Updates any physical items that were modified
 */
function updatePhysicalItems({
  packageId,
  childPackageNumber,
  currentPackagesState,
  sku,
  currentInventoryState,
  updatedInventoryState,
}: {
  packageId: number;
  childPackageNumber?: number;
  currentPackagesState?: Record<string, any>;
  sku: string;
  currentInventoryState: Record<string, any>;
  updatedInventoryState: Record<string, any>;
}) {
  return (dispatch) => {
    dispatch(
      updatePackages({
        currentPackagesState,
        packageNum: packageId,
        childPackageNumber,
        collection: packageCollectionTypes.ITEMS,
        sku,
        itemProperty: 'physicalItems',
        value: Object.keys(updatedInventoryState).reduce((acc, cur, index) => {
          if (currentInventoryState[cur]) {
            acc[cur] = updatedInventoryState[cur];
          }
          return acc;
        }, {}),
      }),
    );
  };
}

/**
 * Updates any physical items that were modified and adds physical items from the item when changing the qty with inventory tracking
 */
function updateAndAddPhysicalItems({
  packageId,
  childPackageNumber,
  newInventoryStateCount,
  currentInventoryStateCount,
  currentInventoryState,
  updatedInventoryState,
  currentPackagesState,
  sku,
}: {
  packageId: number;
  childPackageNumber: number;
  newInventoryStateCount: number;
  currentInventoryStateCount: number;
  currentInventoryState: object;
  updatedInventoryState: object;
  currentPackagesState: object;
  sku: string;
}) {
  return (dispatch) => {
    // Get the amount of new physical items from the `physicalItems` array for this sku and remove them from the array
    // For now we are returning any random items. TODO handle for when editing closed orders
    const dbPhysicalItems = dispatch(
      getAndUpdateItemPhysicalItems(
        sku,
        newInventoryStateCount - currentInventoryStateCount,
      ),
    );
    dispatch(
      updatePackages({
        currentPackagesState: currentPackagesState,
        packageNum: packageId,
        childPackageNumber,
        collection: packageCollectionTypes.ITEMS,
        sku,
        itemProperty: 'physicalItems',
        value: Object.keys(updatedInventoryState).reduce((acc, cur, index) => {
          if (currentInventoryState[cur]) {
            // Already in there so just edit
            acc[cur] = updatedInventoryState[cur];
          } else {
            // Add the new one
            const dbPhysicalItem = dbPhysicalItems.shift() || {};
            const updatedInventory = updatedInventoryState[cur];
            acc[dbPhysicalItem.id ?? makeuuid(6)] = {
              ...dbPhysicalItem,
              ...updatedInventory,
              serialNumber:
                updatedInventory.serialNumber ||
                dbPhysicalItem.serialNumber ||
                '',
            };
          }
          return acc;
        }, {}),
      }),
    );
  };
}

/**
 * Updates any physical items that were modified and remove physical items from the item when changing the qty with inventory tracking
 * and return the items to the global physicalItems collection for this sku
 * @param {object} params
 * @param {object} params.currentPackagesState If the packages were just updated pass in the updated state to ensure that this won't override those changes
 * @param {string[]} params.currentInventoryStateKeys Array of keys for the inventory state before any changes
 * @param {object} params.updatedInventoryState The updated inventory state
 * @param {number} params.packageId
 * @param {number} params.childPackageNumber If it's a nested package (ie on a pallet)
 * @param {string} params.sku
 * @returns
 */
function updateAndRemovePhysicalItems({
  currentInventoryStateKeys,
  currentPackagesState,
  updatedInventoryState,
  packageId,
  childPackageNumber,
  sku,
}) {
  return (dispatch, getState) => {
    const inventoryStateKeys = Object.keys(updatedInventoryState);
    const idsToRemove = currentInventoryStateKeys.filter(
      (key) => !inventoryStateKeys.includes(key),
    );
    // get the physical items from the original list. This will ensure the we reset any edited values
    // on the physical item before we return it to the `physicalItems` array for this sku
    const {
      orders: { selectedOrder },
    } = getState();
    const itemsToRemove = selectedOrder.physicalItems.filter((p) =>
      idsToRemove.includes(p.id + ''),
    );
    // First edit the item's physical items and save the updated packages state
    const newPackageState = dispatch(
      updatePackages({
        currentPackagesState: currentPackagesState,
        packageNum: packageId,
        childPackageNumber,
        collection: packageCollectionTypes.ITEMS,
        sku,
        itemProperty: 'physicalItems',
        value: inventoryStateKeys.reduce((acc, cur, index) => {
          acc[cur] = updatedInventoryState[cur];
          return acc;
        }, {}),
      }),
    );
    // remove the physical items to delete from the item's physical items
    dispatch(
      removePhysicalItemsFromPackage({
        packageId,
        childPackageNumber,
        physicalItemIdsToRemove: idsToRemove,
        sku,
        currentPackagesState: newPackageState,
      }),
    );
    // return the removed items to the `physicalItems` array
    dispatch(addToPhysicalItemsState(sku, itemsToRemove));
  };
}

/**
 * Removes physical items from the item when changing the qty manually (not with inventory tracking)
 * and return the items to the global physicalItems collection for this sku
 * @param {object} params
 * @param {object} params.currentPackagesState If the packages were just updated pass in the updated state to ensure that this won't override those changes
 * @param {number} params.qty
 * @param {number} params.packageId
 * @param {number} params.childPackageNumber If it's a nested package (ie on a pallet)
 * @param {string} params.sku
 * @returns
 */
function removePhysicalItems({
  currentPackagesState,
  qty,
  packageId,
  childPackageNumber,
  sku,
}) {
  return (dispatch, getState) => {
    const {
      orders: { packages, selectedOrder = {} },
    } = getState();

    let itemPhysicalItems = packages[packageId].items[sku]?.physicalItems;

    if (childPackageNumber) {
      itemPhysicalItems =
        packages[packageId]?.childPackages?.[childPackageNumber]?.items[sku]
          ?.physicalItems;
    }

    const hasItemPhysicalItems =
      !!itemPhysicalItems && Object.keys(itemPhysicalItems).length;

    if (!hasItemPhysicalItems) return;

    const physicalItemsKeys = Object.keys(itemPhysicalItems);
    if (physicalItemsKeys.length - 1 < qty) return; // TODO
    const idsToRemove = physicalItemsKeys.slice(-qty);
    // get the physical items from the original list. This will ensure the we reset any edited values
    // on the physical item before we return it to the `physicalItems` array for this sku
    const itemsToRemove = selectedOrder.physicalItems.filter((p) =>
      idsToRemove.includes(p.id + ''),
    );

    // remove the physical items to delete from the item's physical items
    dispatch(
      removePhysicalItemsFromPackage({
        packageId,
        childPackageNumber,
        physicalItemIdsToRemove: idsToRemove,
        sku,
        currentPackagesState,
      }),
    );
    // return the removed items to the `physicalItems` array
    dispatch(addToPhysicalItemsState(sku, itemsToRemove));
  };
}

// ============================================================================
// Inventory Pool Functions - For tracking physical item requirements & consumption
// ============================================================================

/**
 * Pure function that builds the inventory pool from physical items.
 * Returns the computed pool and serial-on-outbound SKUs without dispatching.
 */
function buildInventoryPool(
  physicalItems: OrderPhysicalItemAPI[],
  orderItems: OrderItemAPI[],
): { pool: InventoryPool; skusWithSerialOnOutbound: string[] } {
  const pool: InventoryPool = {};

  const skusWithSerialOnOutbound = orderItems
    .filter((i) => i.serialNumberTrackPreference === 'EnableOnOutbound')
    .map((i) => i.sku);

  for (const item of physicalItems) {
    const {
      sku,
      lotNumber: rawLotNumber,
      expirationDate,
      serialNumber: rawSerialNumber,
      quantity = 1,
    } = item;
    const lotNumber = normalizePhysicalItemIdentifier(rawLotNumber);
    const serialNumber = normalizePhysicalItemIdentifier(rawSerialNumber);

    if (!hasPhysicalItemTrackingValues(item)) continue;

    if (!pool[sku]) {
      pool[sku] = {
        required: {
          combinations: [],
          byLot: {},
          byExpiration: {},
          serialNumbers: [],
          totalQty: 0,
        },
        consumed: {
          combinations: [],
          byLot: {},
          byExpiration: {},
          serialNumbers: [],
          totalQty: 0,
        },
        remaining: {
          combinations: [],
          byLot: {},
          byExpiration: {},
          serialNumbers: [],
          totalQty: 0,
        },
      };
    }

    const skuPool = pool[sku];

    // Normalize expiration date
    const expKey = expirationDate
      ? typeof expirationDate === 'string'
        ? expirationDate
        : new Date(expirationDate).toISOString().split('T')[0]
      : null;

    // Group identical DB rows by exact combination. A quantity-bearing
    // combination can later be split across any number of packages.
    const combination: PhysicalItemCombination = {
      lotNumber: lotNumber || '',
      expirationDate: expKey,
      serialNumber: serialNumber || null,
      quantity,
    };
    const existingRequired = skuPool.required.combinations.find(
      (existing) =>
        physicalItemIdentifiersEqual(
          existing.lotNumber,
          combination.lotNumber,
        ) &&
        existing.expirationDate === combination.expirationDate &&
        physicalItemIdentifiersEqual(
          existing.serialNumber,
          combination.serialNumber,
        ),
    );
    const existingRemaining = skuPool.remaining.combinations.find(
      (existing) =>
        physicalItemIdentifiersEqual(
          existing.lotNumber,
          combination.lotNumber,
        ) &&
        existing.expirationDate === combination.expirationDate &&
        physicalItemIdentifiersEqual(
          existing.serialNumber,
          combination.serialNumber,
        ),
    );
    if (existingRequired && existingRemaining) {
      existingRequired.quantity += quantity;
      existingRemaining.quantity += quantity;
    } else {
      skuPool.required.combinations.push(combination);
      skuPool.remaining.combinations.push({ ...combination });
    }

    // Track lot numbers (aggregate by lot for quick lookup)
    if (lotNumber) {
      const lotKey =
        findPhysicalItemIdentifierKey(skuPool.required.byLot, lotNumber) ??
        lotNumber;
      skuPool.required.byLot[lotKey] =
        (skuPool.required.byLot[lotKey] || 0) + quantity;
      // Also set remaining (will be decreased when consumed)
      skuPool.remaining.byLot[lotKey] =
        (skuPool.remaining.byLot[lotKey] || 0) + quantity;
    }

    // Track expiration dates (aggregate by date for quick lookup)
    if (expKey) {
      skuPool.required.byExpiration[expKey] =
        (skuPool.required.byExpiration[expKey] || 0) + quantity;
      // Also set remaining
      skuPool.remaining.byExpiration[expKey] =
        (skuPool.remaining.byExpiration[expKey] || 0) + quantity;
    }

    // Track serial numbers (must all be used, qty always 1)
    if (serialNumber) {
      skuPool.required.serialNumbers.push(serialNumber);
      // Also set remaining
      skuPool.remaining.serialNumbers.push(serialNumber);
    }

    skuPool.required.totalQty += quantity;
    skuPool.remaining.totalQty += quantity;
  }

  return { pool, skusWithSerialOnOutbound };
}

function rebuildInventoryPoolFromPackages(
  inventoryPool: InventoryPool,
  packages: Record<string, any>,
  skusAllowingSerialOnOutbound: string[] = [],
): InventoryPool {
  const rebuiltPool = _clonedeep(inventoryPool);

  Object.values(rebuiltPool).forEach((skuPool) => {
    skuPool.consumed = {
      combinations: [],
      byLot: {},
      byExpiration: {},
      serialNumbers: [],
      totalQty: 0,
    };
    skuPool.remaining = _clonedeep(skuPool.required);
  });

  const visitPackages = (packageCollection: any) => {
    Object.values(packageCollection ?? {}).forEach((orderPackage: any) => {
      const packageItems = Array.isArray(orderPackage?.items)
        ? orderPackage.items
        : Object.values(orderPackage?.items ?? {});

      packageItems.forEach((packageItem: any) => {
        const sku = packageItem.sku;
        const skuPool = rebuiltPool[sku];
        if (!skuPool) return;

        const physicalItems = Array.isArray(packageItem.physicalItems)
          ? packageItem.physicalItems
          : Object.values(packageItem.physicalItems ?? {});

        physicalItems.forEach((physicalItem: any) => {
          const lotNumber = normalizePhysicalItemIdentifier(
            physicalItem.lotNumber,
          );
          const serialNumber = normalizePhysicalItemIdentifier(
            physicalItem.serialNumber,
          );
          const expirationDate = physicalItem.expirationDate
            ? typeof physicalItem.expirationDate === 'string'
              ? physicalItem.expirationDate
              : new Date(physicalItem.expirationDate)
                  .toISOString()
                  .split('T')[0]
            : null;
          const quantity = physicalItem.quantity || 1;
          const matches = (
            combo: PhysicalItemCombination,
            ignoreSerial = false,
          ) =>
            physicalItemIdentifiersEqual(combo.lotNumber, lotNumber) &&
            combo.expirationDate === expirationDate &&
            (ignoreSerial ||
              physicalItemIdentifiersEqual(combo.serialNumber, serialNumber));

          let requiredCombination = skuPool.required.combinations.find(
            (combo) => matches(combo),
          );
          if (
            !requiredCombination &&
            serialNumber &&
            skusAllowingSerialOnOutbound.includes(sku)
          ) {
            requiredCombination = skuPool.required.combinations.find(
              (combo) => !combo.serialNumber && matches(combo, true),
            );
          }
          if (!requiredCombination) return;

          let consumedCombination = skuPool.consumed.combinations.find(
            (combo) =>
              physicalItemIdentifiersEqual(
                combo.lotNumber,
                requiredCombination.lotNumber,
              ) &&
              combo.expirationDate === requiredCombination.expirationDate &&
              physicalItemIdentifiersEqual(
                combo.serialNumber,
                requiredCombination.serialNumber,
              ),
          );
          if (!consumedCombination) {
            consumedCombination = {
              ...requiredCombination,
              quantity: 0,
            };
            skuPool.consumed.combinations.push(consumedCombination);
          }
          consumedCombination.quantity += quantity;
          skuPool.consumed.totalQty += quantity;

          if (requiredCombination.lotNumber) {
            skuPool.consumed.byLot[requiredCombination.lotNumber] =
              (skuPool.consumed.byLot[requiredCombination.lotNumber] || 0) +
              quantity;
          }
          if (requiredCombination.expirationDate) {
            skuPool.consumed.byExpiration[requiredCombination.expirationDate] =
              (skuPool.consumed.byExpiration[
                requiredCombination.expirationDate
              ] || 0) + quantity;
          }
          if (requiredCombination.serialNumber) {
            skuPool.consumed.serialNumbers.push(
              requiredCombination.serialNumber,
            );
          }
        });
      });

      visitPackages(orderPackage?.childPackages);
    });
  };

  visitPackages(packages);

  Object.values(rebuiltPool).forEach((skuPool) => {
    skuPool.remaining.combinations = skuPool.required.combinations
      .map((required) => {
        const consumed = skuPool.consumed.combinations.find(
          (combo) =>
            physicalItemIdentifiersEqual(combo.lotNumber, required.lotNumber) &&
            combo.expirationDate === required.expirationDate &&
            physicalItemIdentifiersEqual(
              combo.serialNumber,
              required.serialNumber,
            ),
        );
        return {
          ...required,
          quantity: Math.max(0, required.quantity - (consumed?.quantity || 0)),
        };
      })
      .filter((combo) => combo.quantity > 0);
    skuPool.remaining.byLot = Object.fromEntries(
      Object.entries(skuPool.required.byLot).map(([lot, required]) => [
        lot,
        Math.max(0, required - (skuPool.consumed.byLot[lot] || 0)),
      ]),
    );
    skuPool.remaining.byExpiration = Object.fromEntries(
      Object.entries(skuPool.required.byExpiration).map(
        ([expiration, required]) => [
          expiration,
          Math.max(
            0,
            required - (skuPool.consumed.byExpiration[expiration] || 0),
          ),
        ],
      ),
    );
    skuPool.remaining.serialNumbers = skuPool.required.serialNumbers.filter(
      (serial) =>
        !includesPhysicalItemIdentifier(skuPool.consumed.serialNumbers, serial),
    );
    skuPool.remaining.totalQty = Math.max(
      0,
      skuPool.required.totalQty - skuPool.consumed.totalQty,
    );
  });

  return rebuiltPool;
}

/**
 * Initialize the inventory pool from DB physical items.
 * This builds the "required" state from what's in the database.
 * @param physicalItems - Array of physical items from the order API
 * @param itemSettings - Map of SKU to item settings (enableSerialOnOutbound, etc.)
 */
function initializeInventoryPool(
  physicalItems: OrderPhysicalItemAPI[],
  orderItems: OrderItemAPI[],
): AppThunk<void> {
  return (dispatch) => {
    const { pool, skusWithSerialOnOutbound } = buildInventoryPool(
      physicalItems,
      orderItems,
    );
    dispatch(setSkusAllowingSerialOnOutbound(skusWithSerialOnOutbound));
    dispatch(setInventoryPool(pool));
  };
}
/**
 * Record consumed inventory when user packs items and update package physical items.
 * Call this when user confirms physical item entries.
 * This function:
 * 1. Validates items against the inventory pool
 * 2. Updates the inventory pool consumed counts
 * 3. Updates the package's physical items
 */
function recordConsumedInventory({
  sku,
  items,
  packageId,
  childPackageNumber,
  currentPackagesState,
  currentInventoryState,
  currentInventoryPool,
  shouldDispatch = true,
}: RecordConsumedInventoryParams): AppThunk<{
  success: boolean;
  errors: string[];
  updatedPackagesState?: Record<string, any>;
  updatedInventoryPool?: InventoryPool;
}> {
  return (dispatch, getState) => {
    const {
      orders: {
        inventoryPool,
        skusAllowingSerialOnOutbound,
        packages: statePackages,
      },
    } = getState();

    const enableSerialOnOutbound = skusAllowingSerialOnOutbound.includes(sku);
    const inventoryPoolToUpdate = currentInventoryPool ?? inventoryPool;
    const skuPool = inventoryPoolToUpdate[sku];

    const errors: string[] = [];

    // Clone the pool for updates (if it exists)
    let updatedPool: InventoryPool | null = null;
    let updatedSkuPool: InventoryRequirements | null = null;

    if (skuPool) {
      updatedPool = _clonedeep(inventoryPoolToUpdate);
      updatedSkuPool = updatedPool[sku];
    }

    // Helper to normalize expiration dates for comparison
    const normalizeExp = (
      exp: string | Date | null | undefined,
    ): string | null => {
      if (!exp) return null;
      return typeof exp === 'string'
        ? exp
        : new Date(exp).toISOString().split('T')[0];
    };

    // Helper to check if two combinations match
    const combinationsMatch = (
      a: PhysicalItemCombination,
      b: {
        lotNumber?: string;
        expirationDate?: string | Date | null;
        serialNumber?: string;
      },
    ): boolean => {
      const aExp = a.expirationDate || null;
      const bExp = normalizeExp(b.expirationDate) || null;

      return (
        physicalItemIdentifiersEqual(a.lotNumber, b.lotNumber) &&
        aExp === bExp &&
        physicalItemIdentifiersEqual(a.serialNumber, b.serialNumber)
      );
    };

    // Normalize lot/serial values before validation and consumption
    const trackedItemIndexes = items
      .map((_, index) => index)
      .filter((index) => hasPhysicalItemTrackingValues(items[index]));
    const normalizedItems = trackedItemIndexes.map((index) => ({
      ...items[index],
      lotNumber: normalizePhysicalItemIdentifier(items[index].lotNumber),
      serialNumber: normalizePhysicalItemIdentifier(items[index].serialNumber),
    }));

    const submittedSerials = new Set<string>();
    // Outbound-only serials may have no inventory pool. Read other packages too,
    // including nested cartons, so pause/reload cannot reset uniqueness checks.
    const otherPackageSerials = new Set<string>();
    const collectOtherSerials = (
      collection: Record<string, any>,
      parentId?: string,
    ) => {
      Object.entries(collection ?? {}).forEach(([id, orderPackage]) => {
        const isTarget =
          String(parentId ?? id) === String(packageId) &&
          (parentId === undefined
            ? !childPackageNumber
            : String(id) === String(childPackageNumber));
        // Appending a scan must also check the destination carton. Only an edit
        // replaces that item's existing entries and may reuse its own serials.
        if (!isTarget || currentInventoryState === undefined) {
          Object.values(orderPackage.items ?? {}).forEach(
            (packageItem: any) => {
              if (packageItem.sku !== sku) return;
              Object.values(packageItem.physicalItems ?? {}).forEach(
                (pi: any) => {
                  const serial = normalizePhysicalItemIdentifier(
                    pi.serialNumber,
                  );
                  if (serial) otherPackageSerials.add(serial.toUpperCase());
                },
              );
            },
          );
        }
        collectOtherSerials(orderPackage.childPackages, parentId ?? id);
      });
    };
    if (normalizedItems.some((item) => item.serialNumber)) {
      collectOtherSerials(currentPackagesState ?? statePackages);
    }
    // Validate items against pool combinations (if pool exists)
    for (const item of normalizedItems) {
      const qty = item.quantity || 1;
      let consumedInventory = false;
      let consumedInventoryItem = item;

      if (item.serialNumber) {
        const serialKey = item.serialNumber.toUpperCase();
        if (qty !== 1) {
          errors.push(
            `Serial number "${item.serialNumber}" must have quantity 1`,
          );
          continue;
        }
        if (
          submittedSerials.has(serialKey) ||
          otherPackageSerials.has(serialKey)
        ) {
          errors.push(
            `Serial number "${item.serialNumber}" has already been packed`,
          );
          continue;
        }
        submittedSerials.add(serialKey);
      }

      if (updatedSkuPool) {
        // For serial numbers, each must be qty=1 and match exactly
        if (item.serialNumber) {
          const isRequired = includesPhysicalItemIdentifier(
            skuPool.required.serialNumbers,
            item.serialNumber,
          );
          const isAlreadyUsed = includesPhysicalItemIdentifier(
            updatedSkuPool.consumed.serialNumbers,
            item.serialNumber,
          );

          if (isAlreadyUsed) {
            errors.push(
              `Serial number "${item.serialNumber}" has already been packed`,
            );
            continue;
          } else if (!isRequired && !enableSerialOnOutbound) {
            errors.push(
              `Serial number "${item.serialNumber}" is not in the inventory and EnableSerialOnOutbound is not enabled`,
            );
            continue;
          }

          // For DB serials: find exact combination match
          const matchingCombination = isRequired
            ? updatedSkuPool.remaining.combinations.find((combo) =>
                combinationsMatch(combo, item),
              )
            : null;

          if (matchingCombination) {
            consumedInventory = true;
            // DB serial: consume from exact combination
            matchingCombination.quantity -= qty;
            if (matchingCombination.quantity <= 0) {
              // Remove from remaining if fully consumed
              updatedSkuPool.remaining.combinations =
                updatedSkuPool.remaining.combinations.filter(
                  (c) => c !== matchingCombination,
                );
            }

            // Add to consumed
            const existingConsumed = updatedSkuPool.consumed.combinations.find(
              (combo) => combinationsMatch(combo, item),
            );
            if (existingConsumed) {
              existingConsumed.quantity += qty;
            } else {
              updatedSkuPool.consumed.combinations.push({
                lotNumber: item.lotNumber || '',
                expirationDate: normalizeExp(item.expirationDate),
                serialNumber: item.serialNumber || null,
                quantity: qty,
              });
            }
          } else if (!isRequired && enableSerialOnOutbound) {
            // New serial via enableSerialOnOutbound
            // Lots and expirations can NEVER be created - they must exist in DB

            // Validate lot exists in DB if provided
            if (item.lotNumber) {
              const lotExists =
                findPhysicalItemIdentifierKey(
                  skuPool.required.byLot,
                  item.lotNumber,
                ) !== undefined;
              if (!lotExists) {
                errors.push(
                  `Lot "${item.lotNumber}" is not in the inventory. Users cannot create new lots.`,
                );
                continue;
              }
            }

            // Validate expiration exists in DB if provided
            if (item.expirationDate) {
              const expKey = normalizeExp(item.expirationDate)!;
              const expExists =
                skuPool.required.byExpiration[expKey] !== undefined;
              if (!expExists) {
                errors.push(
                  `Expiration "${expKey}" is not in the inventory. Users cannot create new expirations.`,
                );
                continue;
              }
            }

            // Try to find an existing lot+exp combination (without serial)
            const matchingLotExpCombo =
              updatedSkuPool.remaining.combinations.find(
                (combo) =>
                  physicalItemIdentifiersEqual(
                    combo.lotNumber,
                    item.lotNumber,
                  ) &&
                  combo.expirationDate === normalizeExp(item.expirationDate) &&
                  !combo.serialNumber, // Only match non-serial combinations
              );

            if (matchingLotExpCombo) {
              consumedInventory = true;
              consumedInventoryItem = {
                ...item,
                serialNumber: '',
              };
              // Found existing lot+exp combo - consume from it
              if (matchingLotExpCombo.quantity < qty) {
                errors.push(
                  `Lot+Expiration combination has only ${matchingLotExpCombo.quantity} available but trying to pack ${qty}`,
                );
                continue;
              }

              matchingLotExpCombo.quantity -= qty;
              if (matchingLotExpCombo.quantity <= 0) {
                updatedSkuPool.remaining.combinations =
                  updatedSkuPool.remaining.combinations.filter(
                    (c) => c !== matchingLotExpCombo,
                  );
              }
            } else if (!item.lotNumber && !item.expirationDate) {
              // No lot and no expiration - creating new serial-only row is OK
              // This is the only case where a new row can be created
            } else {
              // Has lot or expiration but no matching combination
              const lotPart = item.lotNumber
                ? `lot "${item.lotNumber}"`
                : 'no lot';
              const expPart = item.expirationDate
                ? `expiration "${normalizeExp(item.expirationDate)}"`
                : 'no expiration';
              errors.push(
                `Lot+Expiration combination (${lotPart}, ${expPart}) is not available in the inventory`,
              );
              continue;
            }

            if (consumedInventory) {
              const existingConsumed =
                updatedSkuPool.consumed.combinations.find((combo) =>
                  combinationsMatch(combo, consumedInventoryItem),
                );
              if (existingConsumed) {
                existingConsumed.quantity += qty;
              } else {
                updatedSkuPool.consumed.combinations.push({
                  lotNumber: consumedInventoryItem.lotNumber || '',
                  expirationDate: normalizeExp(
                    consumedInventoryItem.expirationDate,
                  ),
                  serialNumber: null,
                  quantity: qty,
                });
              }
            }
          } else if (isRequired) {
            // DB serial but no matching combination found
            errors.push(
              `Serial "${item.serialNumber}" exists in DB but the combination with the provided lot/expiration is not available`,
            );
            continue;
          }

          // Update consumed serial numbers list
          updatedSkuPool.consumed.serialNumbers.push(item.serialNumber);
          // Update remaining only for DB-required serials
          if (isRequired) {
            updatedSkuPool.remaining.serialNumbers =
              updatedSkuPool.remaining.serialNumbers.filter(
                (s) => !physicalItemIdentifiersEqual(s, item.serialNumber),
              );
          }
        } else {
          // Non-serial item: find matching combination by lot + expiration
          const matchingCombination =
            updatedSkuPool.remaining.combinations.find((combo) =>
              combinationsMatch(combo, item),
            );

          if (!matchingCombination) {
            const lotPart = item.lotNumber
              ? `lot "${item.lotNumber}"`
              : 'no lot';
            const expPart = item.expirationDate
              ? `expiration "${normalizeExp(item.expirationDate)}"`
              : 'no expiration';
            errors.push(
              `Combination (${lotPart}, ${expPart}) is not available in the inventory`,
            );
            continue;
          }

          if (matchingCombination.quantity < qty) {
            errors.push(
              `Combination has only ${matchingCombination.quantity} available but trying to pack ${qty}`,
            );
            continue;
          }

          consumedInventory = true;
          // Consume from this combination
          matchingCombination.quantity -= qty;
          if (matchingCombination.quantity <= 0) {
            // Remove from remaining if fully consumed
            updatedSkuPool.remaining.combinations =
              updatedSkuPool.remaining.combinations.filter(
                (c) => c !== matchingCombination,
              );
          }

          // Add to consumed
          const existingConsumed = updatedSkuPool.consumed.combinations.find(
            (combo) => combinationsMatch(combo, item),
          );
          if (existingConsumed) {
            existingConsumed.quantity += qty;
          } else {
            updatedSkuPool.consumed.combinations.push({
              lotNumber: item.lotNumber || '',
              expirationDate: normalizeExp(item.expirationDate),
              serialNumber: null,
              quantity: qty,
            });
          }
        }

        // Only stock-backed PI changes the inventory pool totals. Package-only
        // Enable-on-Outbound serials are tracked in the package payload only.
        if (consumedInventory && consumedInventoryItem.lotNumber) {
          const lotKey =
            findPhysicalItemIdentifierKey(
              skuPool.required.byLot,
              consumedInventoryItem.lotNumber,
            ) ?? consumedInventoryItem.lotNumber;
          updatedSkuPool.consumed.byLot[lotKey] =
            (updatedSkuPool.consumed.byLot[lotKey] || 0) + qty;
          updatedSkuPool.remaining.byLot[lotKey] = Math.max(
            0,
            (updatedSkuPool.remaining.byLot[lotKey] || 0) - qty,
          );
        }

        if (consumedInventory && consumedInventoryItem.expirationDate) {
          const expKey = normalizeExp(consumedInventoryItem.expirationDate)!;
          updatedSkuPool.consumed.byExpiration[expKey] =
            (updatedSkuPool.consumed.byExpiration[expKey] || 0) + qty;
          updatedSkuPool.remaining.byExpiration[expKey] = Math.max(
            0,
            (updatedSkuPool.remaining.byExpiration[expKey] || 0) - qty,
          );
        }

        if (consumedInventory) {
          updatedSkuPool.consumed.totalQty += qty;
          updatedSkuPool.remaining.totalQty = Math.max(
            0,
            updatedSkuPool.remaining.totalQty - qty,
          );
        }
      } else if (item.serialNumber && !enableSerialOnOutbound) {
        // No pool but trying to add serial without enableSerialOnOutbound
        errors.push(
          `Serial number "${item.serialNumber}" cannot be added - no inventory pool and EnableSerialOnOutbound is not enabled`,
        );
      } else if (item.lotNumber || item.expirationDate) {
        errors.push('Lot/expiration tracking is not in the picked inventory');
      }
    }

    if (errors.length > 0) {
      return { success: false, errors };
    }

    // Build the new physical items object for the package
    // When editing, preserve existing keys; for new items, generate UUIDs
    const currentInventoryStateKeys = Object.keys(currentInventoryState || {});
    const newPhysicalItems: Record<string, any> = {};

    normalizedItems.forEach((item, index) => {
      // Use existing key if editing, otherwise generate new UUID
      const itemKey =
        currentInventoryStateKeys[trackedItemIndexes[index]] ?? makeuuid(6);
      const existingItem = currentInventoryState?.[itemKey] || {};

      newPhysicalItems[itemKey] = {
        ...existingItem,
        lotNumber: item.lotNumber || '',
        expirationDate: item.expirationDate || '',
        serialNumber: item.serialNumber || '',
        quantity: item.quantity || 1,
      };
    });

    // Update package physical items
    const updatedPackagesState = dispatch(
      updatePackages({
        currentPackagesState: currentPackagesState || statePackages,
        packageNum: +packageId,
        childPackageNumber: childPackageNumber
          ? +childPackageNumber
          : undefined,
        collection: packageCollectionTypes.ITEMS,
        sku,
        itemProperty: 'physicalItems',
        value: newPhysicalItems,
        shouldOverrideValue: currentInventoryState !== undefined,
        shouldDispatch,
      }),
    );

    // Update inventory pool if it exists
    if (updatedPool && shouldDispatch) {
      dispatch(setInventoryPool(updatedPool));
    }

    return {
      success: true,
      errors: [],
      updatedPackagesState,
      updatedInventoryPool: updatedPool ?? inventoryPoolToUpdate,
    };
  };
}

/**
 * Remove consumed inventory when user edits/removes packed items and update package.
 * Call this when user removes physical items from a package.
 * This function:
 * 1. Updates the inventory pool to release consumed counts
 * 2. Clears the package's physical items for this SKU
 */
function removeConsumedInventory({
  sku,
  items,
  packageId,
  childPackageNumber,
  currentPackagesState,
  clearPackagePhysicalItems = true,
  shouldDispatch = true,
}: RemoveConsumedInventoryParams): AppThunk<{
  updatedPackagesState?: Record<string, any>;
  updatedInventoryPool: InventoryPool;
}> {
  return (dispatch, getState) => {
    const {
      orders: {
        inventoryPool,
        packages: statePackages,
        skusAllowingSerialOnOutbound,
      },
    } = getState();

    const skuPool = inventoryPool[sku];
    let updatedPool = inventoryPool;

    // Update pool if it exists
    if (skuPool) {
      updatedPool = _clonedeep(inventoryPool);
      const updatedSkuPool = updatedPool[sku];

      // Helper to normalize expiration dates for comparison
      const normalizeExp = (
        exp: string | Date | null | undefined,
      ): string | null => {
        if (!exp) return null;
        return typeof exp === 'string'
          ? exp
          : new Date(exp).toISOString().split('T')[0];
      };

      // Helper to check if two combinations match
      const combinationsMatch = (
        a: PhysicalItemCombination,
        b: {
          lotNumber?: string;
          expirationDate?: string | Date | null;
          serialNumber?: string;
        },
      ): boolean => {
        const aExp = a.expirationDate || null;
        const bExp = normalizeExp(b.expirationDate) || null;

        return (
          physicalItemIdentifiersEqual(a.lotNumber, b.lotNumber) &&
          aExp === bExp &&
          physicalItemIdentifiersEqual(a.serialNumber, b.serialNumber)
        );
      };

      // Normalize lot/serial values before release/restore
      const normalizedItems = items.map((item) => ({
        ...item,
        lotNumber: normalizePhysicalItemIdentifier(item.lotNumber),
        serialNumber: normalizePhysicalItemIdentifier(item.serialNumber),
      }));

      for (const item of normalizedItems) {
        const qty = item.quantity || 1;
        const matchesRequired = (combo: PhysicalItemCombination) =>
          combinationsMatch(combo, item);
        let requiredCombo = skuPool.required.combinations.find(matchesRequired);
        let inventoryItem = item;

        if (
          !requiredCombo &&
          item.serialNumber &&
          skusAllowingSerialOnOutbound.includes(sku)
        ) {
          inventoryItem = { ...item, serialNumber: '' };
          requiredCombo = skuPool.required.combinations.find((combo) =>
            combinationsMatch(combo, inventoryItem),
          );
        }

        // Find and update the consumed combination
        const consumedCombo = updatedSkuPool.consumed.combinations.find(
          (combo) => combinationsMatch(combo, inventoryItem),
        );

        if (consumedCombo) {
          consumedCombo.quantity -= qty;
          if (consumedCombo.quantity <= 0) {
            // Remove from consumed if fully released
            updatedSkuPool.consumed.combinations =
              updatedSkuPool.consumed.combinations.filter(
                (c) => c !== consumedCombo,
              );
          }
        }

        // Restore to remaining combinations (find or create)
        const remainingCombo = updatedSkuPool.remaining.combinations.find(
          (combo) => combinationsMatch(combo, inventoryItem),
        );

        if (requiredCombo && remainingCombo) {
          // Restore qty (up to required amount)
          const maxQty = requiredCombo.quantity;
          remainingCombo.quantity = Math.min(
            maxQty,
            remainingCombo.quantity + qty,
          );
        } else {
          // Re-add to remaining if it was fully consumed before
          if (requiredCombo) {
            updatedSkuPool.remaining.combinations.push({
              lotNumber: inventoryItem.lotNumber || '',
              expirationDate: normalizeExp(inventoryItem.expirationDate),
              serialNumber: inventoryItem.serialNumber || null,
              quantity: Math.min(requiredCombo.quantity, qty),
            });
          }
        }

        // Update the quick-lookup maps (byLot, byExpiration, serialNumbers)
        const lotKey = inventoryItem.lotNumber
          ? findPhysicalItemIdentifierKey(
              skuPool.required.byLot,
              inventoryItem.lotNumber,
            )
          : undefined;
        if (requiredCombo && lotKey && updatedSkuPool.consumed.byLot[lotKey]) {
          updatedSkuPool.consumed.byLot[lotKey] = Math.max(
            0,
            updatedSkuPool.consumed.byLot[lotKey] - qty,
          );
          // Restore remaining (up to required amount)
          const requiredQty = skuPool.required.byLot[lotKey] || 0;
          updatedSkuPool.remaining.byLot[lotKey] = Math.min(
            requiredQty,
            (updatedSkuPool.remaining.byLot[lotKey] || 0) + qty,
          );
        }

        if (requiredCombo && inventoryItem.expirationDate) {
          const expKey = normalizeExp(inventoryItem.expirationDate);
          if (expKey && updatedSkuPool.consumed.byExpiration[expKey]) {
            updatedSkuPool.consumed.byExpiration[expKey] = Math.max(
              0,
              updatedSkuPool.consumed.byExpiration[expKey] - qty,
            );
            // Restore remaining (up to required amount)
            const requiredQty = skuPool.required.byExpiration[expKey] || 0;
            updatedSkuPool.remaining.byExpiration[expKey] = Math.min(
              requiredQty,
              (updatedSkuPool.remaining.byExpiration[expKey] || 0) + qty,
            );
          }
        }

        // Remove serial from consumed list and restore to remaining if it was required
        if (item.serialNumber) {
          const idx = updatedSkuPool.consumed.serialNumbers.findIndex(
            (serial) => physicalItemIdentifiersEqual(serial, item.serialNumber),
          );
          if (idx !== -1) {
            updatedSkuPool.consumed.serialNumbers.splice(idx, 1);
            // Restore to remaining only if it was a required serial
            const requiredSerial = skuPool.required.serialNumbers.find(
              (serial) =>
                physicalItemIdentifiersEqual(serial, item.serialNumber),
            );
            if (
              requiredSerial &&
              !includesPhysicalItemIdentifier(
                updatedSkuPool.remaining.serialNumbers,
                requiredSerial,
              )
            ) {
              updatedSkuPool.remaining.serialNumbers.push(requiredSerial);
            }
          }
        }

        if (requiredCombo) {
          updatedSkuPool.consumed.totalQty = Math.max(
            0,
            updatedSkuPool.consumed.totalQty - qty,
          );
          // Restore remaining totalQty (up to required amount)
          updatedSkuPool.remaining.totalQty = Math.min(
            skuPool.required.totalQty,
            updatedSkuPool.remaining.totalQty + qty,
          );
        }
      }

      if (shouldDispatch) dispatch(setInventoryPool(updatedPool));
    }

    // Editing clears the old package PI before replacement. Package/item
    // deletion already removed the containing state and must not recreate it.
    const updatedPackagesState = clearPackagePhysicalItems
      ? dispatch(
          updatePackages({
            shouldOverrideValue: true,
            currentPackagesState: currentPackagesState || statePackages,
            packageNum: +packageId,
            childPackageNumber: childPackageNumber
              ? +childPackageNumber
              : undefined,
            collection: packageCollectionTypes.ITEMS,
            sku,
            itemProperty: 'physicalItems',
            value: {},
            shouldDispatch,
          }),
        )
      : currentPackagesState || statePackages;

    return { updatedPackagesState, updatedInventoryPool: updatedPool };
  };
}

/**
 * Get remaining required inventory for a SKU.
 * Useful for showing user what's left to pack.
 * @param sku - The SKU to check
 */
function getRemainingRequired(
  sku: string,
): AppThunk<RemainingPhysicalItemsRequiredType> {
  return (_, getState) => {
    const {
      orders: { inventoryPool },
    } = getState();

    const skuPool = inventoryPool[sku];
    if (!skuPool) {
      return { lots: {}, expirations: {}, serials: [], totalQty: 0 };
    }

    const { required, consumed } = skuPool;
    const lots: Record<string, number> = {};
    const expirations: Record<string, number> = {};

    // Calculate remaining lots
    for (const [lot, requiredQty] of Object.entries(required.byLot)) {
      const consumedQty = consumed.byLot[lot] || 0;
      const remaining = requiredQty - consumedQty;
      if (remaining > 0) {
        lots[lot] = remaining;
      }
    }

    // Calculate remaining expirations
    for (const [exp, requiredQty] of Object.entries(required.byExpiration)) {
      const consumedQty = consumed.byExpiration[exp] || 0;
      const remaining = requiredQty - consumedQty;
      if (remaining > 0) {
        expirations[exp] = remaining;
      }
    }

    // Calculate remaining serials
    const serials = required.serialNumbers.filter(
      (s) => !includesPhysicalItemIdentifier(consumed.serialNumbers, s),
    );

    // Calculate remaining total qty
    const totalQty = required.totalQty - consumed.totalQty;

    return { lots, expirations, serials, totalQty: Math.max(0, totalQty) };
  };
}

// ============================================================================

/**
 * Process an order
 * @param {object} params
 * @param {object} params.payload
 * @param {object} [params.token] Use if we need an admin token
 * @returns {normalizeReturn}
 */
function processOrder({ payload, token }) {
  return async (dispatch) => {
    if (token) {
      const { error, data } = await dispatch(
        customPost({
          url: 'orders/process',
          body: payload,
          authToken: token,
        }),
      );
      if (error) return { error };
      return { data };
    } else {
      const { data, error } = await authPost(`orders/process/`, payload);
      if (error) return { error };
      const validationMessage = data.validationMessages?.[0];
      if (validationMessage) {
        // dispatch(setSelectedOrder(data));
        // dispatch(setCanOnlyShip(true));
        return { error: validationMessage };
      }
      return { data };
    }
  };
}

/**
 * Process an order
 * @param {object} params
 * @param {object} params.payload
 * @returns {normalizeReturn}
 */
function processFreightOrder({ payload, showLoader = true, token }) {
  return async (dispatch) => {
    showLoader && dispatch(setUILoading(true));
    if (token) {
      const { error, data } = await dispatch(
        customPost({
          url: 'orders/process/freight',
          body: payload,
          authToken: token,
        }),
      );
      showLoader && dispatch(setUILoading(false));
      if (error) return { error };
      return { data };
    } else {
      const { data, error } = await authPost(`orders/process/freight`, payload);
      showLoader && dispatch(setUILoading(false));
      if (error) return { error };
      const validationMessage = data.validationMessages?.[0];
      if (validationMessage) {
        // dispatch(setSelectedOrder(data));
        // dispatch(setCanOnlyShip(true));
        return { error: validationMessage };
      }
      return { data };
    }
  };
}

/**
 * Process an order
 */
function processReship({
  orderId,
  payload,
}: {
  orderId: number;
  payload: any;
}): AppThunk<Promise<NormalizeReturn<ProcessReshipReturnAPI>>> {
  return async (dispatch) => {
    const { data, error } = await authPost<ProcessReshipReturnAPI>(
      `orders/${orderId}/reship`,
      payload,
    );
    if (error) {
      dispatch(showError({ message: error.message }));
      return { error };
    }
    const validationMessage = data.validationMessages?.[0];
    if (validationMessage) {
      dispatch(showError({ message: validationMessage.message }));
      // save the created entity ids
      const entityIds: Partial<ProcessReshipReturnAPI> = {
        overrideOrderShipmentID: data.overrideOrderShipmentID,
      };
      if (data.shipToContactOverride) {
        entityIds.shipToContactID = data.shipToContactOverride.id;
      }
      dispatch(updateReshipProperties({ entityIds }));
      return {
        error: {
          code: 'VALIDATION_ERROR',
          message: validationMessage.message,
        },
      };
    }
    dispatch(
      showNotification({ message: 'Successfully processed the reship.' }),
    );
    return { data };
  };
}

function printLabels(
  orderId: number | string,
  shipmentId: number | string,
  printMethod = 'PrintNode',
) {
  return async (dispatch, getState) => {
    const {
      systemPersist: { machineName },
    } = getState();
    dispatch(setUILoading(true));
    const { data, error } = await authPost(
      [`orders/${orderId}/print-labels/${shipmentId}`, { machineName }],
      { printMethod },
    );
    dispatch(setUILoading(false));
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'Error printing labels',
        }),
      );
      return { error };
    }
    dispatch(showNotification({ message: 'Labels printing' }));
    return { data };
  };
}

function printOrderCustomsForm(orderId, shipmentId) {
  return async (dispatch, getState) => {
    const { data, error } = await dispatch(
      addResource({
        baseUrl: `orders/${orderId}/print-customs-form/${shipmentId}`,
        payload: {},
        shouldSetUILoading: true,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

function printPalletLabels(orderId) {
  return async (dispatch, getState) => {
    const {
      systemPersist: { machineName },
    } = getState();
    dispatch(setUILoading(true));
    const { data, error } = await authPost(
      [`orders/${orderId}/freight/print-pallet-labels`, { machineName }],
      {},
    );
    dispatch(setUILoading(false));
    if (error) return { error };

    dispatch(setUILoading(false));
    if (error) return { error };
    return { data };
  };
}

function printLabelsByExternalId(orderExternalId: number | string) {
  return async (dispatch, getState) => {
    const {
      systemPersist: { machineName },
    } = getState();
    const { data, error } = await authPost(
      [`orders/${orderExternalId}/print-labels`, { machineName }],
      {},
    );
    if (error) return { error };
    return { data };
  };
}

function voidShipment(
  orderId: number | string,
  shipmentId: number | string,
  payload = {},
  tokenOverride,
) {
  return async (dispatch, getState) => {
    let data;
    let error;
    const url = `orders/${orderId}/shipment/${shipmentId}/cancel`;

    if (tokenOverride) {
      const { data: responseData, error: errorResponse } = await dispatch(
        customPost({
          url,
          authToken: tokenOverride,
          body: payload,
        }),
      );
      data = responseData;
      error = errorResponse;
    } else {
      const { data: responseData, error: errorResponse } = await authPost(
        url,
        payload,
      );
      data = responseData;
      error = errorResponse;
    }
    if (error) {
      return {
        error,
      };
    }
    dispatch(showNotification({ message: 'Successfully voided the shipment' }));
    return { data };
  };
}

function removeRecordedReturn(
  orderId: number | string,
  orderShipmentId: number | string,
) {
  return async (dispatch) => {
    const { data, error } = await authDelete(
      `orders/${orderId}/returns/${orderShipmentId}`,
    );
    if (error) {
      return { error };
    }
    dispatch(showNotification({ message: 'Return removed' }));
    return { data };
  };
}

function getOrderAggregatedFeesTotals(orderId: number | string) {
  return async (dispatch, getState) => {
    const { data, error } = await authGet(`orders/${orderId}/fees/aggregated`);
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    return { data };
  };
}

function getOrderProcessingTimeline(orderId: number | string) {
  return async (dispatch, getState) => {
    const { data, error } = await authGet(
      `orders/${orderId}/processing-timeline`,
    );
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    return { data };
  };
}

function getOrderTotalProcessingTime(orderId) {
  return async (dispatch, getState) => {
    const { data, error } = await authGet(
      `orders/${orderId}/total-processing-time`,
    );
    if (error) {
      return {
        error,
      };
    }
    return { data };
  };
}

function runOrdersImport() {
  return async (dispatch) => {
    const { data, error } = await authPost('/orders/sync-wms-orders', {});
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'Something went wrong',
        }),
      );
      return {
        error,
      };
    }
    dispatch(showNotification({ message: 'Order syncing is in progress' }));
    return { data };
  };
}

function cancelOrder({
  orderId,
  token,
  payload,
}: {
  orderId: number | string;
  token?: string;
  payload?: object;
}) {
  return async (dispatch, getState) => {
    if (token) {
      const { error, data } = await dispatch(
        customPost({
          url: `orders/${orderId}/cancel`,
          authToken: token,
          body: payload,
        }),
      );
      if (error) return { error };
      return { data };
    } else {
      const { data, error } = await authPost(
        `orders/${orderId}/cancel`,
        payload,
      );
      if (error) {
        dispatch(showError({ message: error.message }));
        return {
          error,
        };
      }
      return { data };
    }
  };
}

function batchCancelOrders({
  orderIds,
  token,
  payload,
}: {
  orderIds: Array<number | string>;
  token?: string;
  payload: { reason: string };
}) {
  return async (dispatch) => {
    const body = {
      orderIDs: orderIds,
      reason: payload.reason,
    };

    if (token) {
      const { error, data } = await dispatch(
        customPost({
          url: 'orders/cancel/batch',
          authToken: token,
          body,
        }),
      );
      if (error) {
        return { error };
      }
      return { data };
    }

    const { data, error } = await authPost('orders/cancel/batch', body);
    if (error) {
      dispatch(showError({ message: error.message }));
      return { error };
    }
    return { data };
  };
}

function batchAutoAllocateOrders({
  orderIds,
}: {
  orderIds: Array<number | string>;
}) {
  return async (dispatch) => {
    const { data, error } = await authPost<{
      processedCount: number;
      skippedOrders: Array<{ orderID: number; reason: string }>;
    }>('orders/auto-allocate/batch', {
      orderIDs: orderIds,
    });
    if (error) {
      dispatch(
        showError({
          message:
            error.message || error.title || 'Failed to auto-allocate orders',
        }),
      );
      return { error };
    }
    return { data };
  };
}

function mergeOrders({
  orderIDs,
  masterOrderID,
}: {
  orderIDs: number[];
  masterOrderID?: number;
}) {
  return async (dispatch) => {
    const body = {
      orderIDs,
      masterOrderID,
    };

    const { data, error } = await authPost('merged-orders', body);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'Failed to merge orders',
        }),
      );
      return { error };
    }
    return { data };
  };
}

function getMergedOrderGroup(groupID: number) {
  return async (dispatch) => {
    const { data, error } = await authGet(`merged-orders/${groupID}`);
    if (error) {
      return { error };
    }
    return { data };
  };
}

function clearOrderPackages({
  orderId,
  token,
}: {
  orderId: number | string;
  token?: string;
}) {
  return async (dispatch, getState) => {
    if (token) {
      const { error, data } = await dispatch(
        customPost({
          url: `orders/${orderId}/clear-packages`,
          authToken: token,
          body: {},
        }),
      );
      if (error) {
        dispatch(showError({ message: error.message }));
        return { error };
      }
      dispatch(showNotification({ message: 'Changes saved' }));
      return { data };
    } else {
      const { data, error } = await authPost(
        `orders/${orderId}/clear-packages`,
        {},
      );
      if (error) {
        dispatch(showError({ message: error.message }));
        return {
          error,
        };
      }
      dispatch(showNotification({ message: 'Changes saved' }));
      return { data };
    }
  };
}

function pauseOrder(orderExternalId, payload) {
  return async (dispatch, getState) => {
    const { data, error } = await authPost(
      `orders/${orderExternalId}/pause-order`,
      payload,
    );
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    return { data };
  };
}

const FULFILLMENT_CACHE_MINUTES = 3;

function buildFulfillmentCacheKey(
  params: OrderFulfillmentScheduleRequest,
): string {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce(
      (acc, key) => {
        const value = params[key as keyof OrderFulfillmentScheduleRequest];
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>,
    );
  return JSON.stringify(sortedParams);
}

function getOrderFulfillmentSchedule(
  params: OrderFulfillmentScheduleRequest,
  ignoreCache: boolean = false,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch, getState) => {
    const cacheKey = buildFulfillmentCacheKey(params);
    const { orders: { fulfillmentScheduleCache } = {} } = getState();
    const cached = fulfillmentScheduleCache?.[cacheKey];

    if (
      !ignoreCache &&
      cached &&
      !!cached.fetchedOn &&
      isBefore(
        new Date(),
        addMinutes(parseISO(cached.fetchedOn), FULFILLMENT_CACHE_MINUTES),
      )
    ) {
      return { data: cached.data };
    }

    const { data, error } = await authGet([
      'reports/orders/fulfillment-schedule',
      params,
    ]);
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    dispatch(setFulfillmentScheduleCache({ cacheKey, data }));
    return { data };
  };
}

function getOrderFulfillmentProgress(
  params: OrderFulfillmentScheduleRequest,
  ignoreCache: boolean = false,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch, getState) => {
    const cacheKey = buildFulfillmentCacheKey(params);
    const { orders: { fulfillmentProgressCache } = {} } = getState();
    const cached = fulfillmentProgressCache?.[cacheKey];

    if (
      !ignoreCache &&
      cached &&
      !!cached.fetchedOn &&
      isBefore(
        new Date(),
        addMinutes(parseISO(cached.fetchedOn), FULFILLMENT_CACHE_MINUTES),
      )
    ) {
      return { data: cached.data };
    }

    const { data, error } = await authGet([
      'reports/orders/fulfillment-progress',
      params,
    ]);
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    dispatch(setFulfillmentProgressCache({ cacheKey, data }));
    return { data };
  };
}

function getOrderFulfillmentExpressShipmentsProgress(
  params: OrderFulfillmentScheduleRequest,
  ignoreCache: boolean = false,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch, getState) => {
    const cacheKey = buildFulfillmentCacheKey(params);
    const { orders: { fulfillmentExpressShipmentsCache } = {} } = getState();
    const cached = fulfillmentExpressShipmentsCache?.[cacheKey];

    if (
      !ignoreCache &&
      cached &&
      !!cached.fetchedOn &&
      isBefore(
        new Date(),
        addMinutes(parseISO(cached.fetchedOn), FULFILLMENT_CACHE_MINUTES),
      )
    ) {
      return { data: cached.data };
    }

    const { data, error } = await authGet([
      'reports/orders/fulfillment-express-shipments',
      params,
    ]);
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    dispatch(setFulfillmentExpressShipmentsCache({ cacheKey, data }));
    return { data };
  };
}

function getOrderFulfillmentOpenOrdersPerCustomer(
  params: OrderFulfillmentScheduleRequest,
  ignoreCache: boolean = false,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch, getState) => {
    const cacheKey = buildFulfillmentCacheKey(params);
    const { orders: { fulfillmentOpenOrdersPerCustomerCache } = {} } =
      getState();
    const cached = fulfillmentOpenOrdersPerCustomerCache?.[cacheKey];

    if (
      !ignoreCache &&
      cached &&
      !!cached.fetchedOn &&
      isBefore(
        new Date(),
        addMinutes(parseISO(cached.fetchedOn), FULFILLMENT_CACHE_MINUTES),
      )
    ) {
      return { data: cached.data };
    }

    const { data, error } = await authGet([
      'reports/orders/fulfillment/open-per-customer',
      params,
    ]);
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    dispatch(setFulfillmentOpenOrdersPerCustomerCache({ cacheKey, data }));
    return { data };
  };
}

function ensurePropertyArray(property: any) {
  if (!property) return [];
  return Array.isArray(property) ? property : [property];
}

function placeOrderOnCustomerHold(orderId: number | string, note?: string) {
  return async (dispatch, getState) => {
    const { data, error } = await authPost(`orders/${orderId}/status`, {
      status: 'CustomerHold',
      note,
    });
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    return { data };
  };
}

function releaseOrderCustomerHold(orderId: number | string, note?: string) {
  return async (dispatch, getState) => {
    const { data, error } = await authPost(
      `orders/${orderId}/release-customer-hold`,
      { note },
    );
    if (error) {
      dispatch(showError({ message: error.message }));
      return {
        error,
      };
    }
    return { data };
  };
}

function printPackageLabels({
  orderId,
  orderPackageId,
  payload,
}: {
  orderId: number | string;
  orderPackageId: number;
  payload: PrintOrderPackageLabelRequest;
}): AppThunk<Promise<NormalizeReturn<PrintOrderPackageLabelResponse>>> {
  return async (dispatch, getState) => {
    const {
      systemPersist: { machineName },
    } = getState();
    const { data, error } = await dispatch(
      addResource<PrintOrderPackageLabelResponse>({
        baseUrl: `orders/${orderId}/order-package/${orderPackageId}/print-labels`,
        payload: {
          ...payload,
          machineName: payload.printWithPrintNode ? machineName : undefined,
        },
        shouldSetUILoading: true,
        shouldShowErrorMessage: false,
      }),
    );
    if (error) {
      return {
        error,
      };
    }
    return { data };
  };
}

function sealPackageAndPrintLabel({
  orderId,
  packagePayload,
}: {
  orderId: number | string;
  packagePayload: Record<string, any>;
}): AppThunk<Promise<NormalizeReturn<PrintOrderPackageLabelResponse>>> {
  return async (dispatch, getState) => {
    const {
      systemPersist: { machineName },
    } = getState();
    const { data, error } = await authPost(
      `orders/${orderId}/seal-package?machineName=${encodeURIComponent(machineName || '')}`,
      packagePayload,
    );
    if (error) {
      // Sealing commits before printing. Preserve the returned package identity
      // so a print failure can be retried without creating a second package.
      const printFailure = error as typeof error &
        Partial<PrintOrderPackageLabelResponse>;
      dispatch(
        showError({
          message:
            error.message ||
            error.title ||
            (Array.isArray(printFailure.errors)
              ? printFailure.errors.join(', ')
              : '') ||
            'Failed to seal package and print label',
        }),
      );
      return {
        error,
        data: printFailure.orderPackageID
          ? (printFailure as PrintOrderPackageLabelResponse)
          : undefined,
      };
    }
    return { data };
  };
}

function printSinglePackageIdentificationLabel({
  orderId,
  packageId,
}: {
  orderId: number | string;
  packageId: number;
}): AppThunk<Promise<NormalizeReturn<PrintOrderPackageLabelResponse>>> {
  return async (dispatch, getState) => {
    const {
      systemPersist: { machineName },
    } = getState();
    const { data, error } = await authPost(
      `orders/${orderId}/packages/${packageId}/identification-label?machineName=${encodeURIComponent(machineName || '')}`,
      {},
    );
    if (error) {
      dispatch(
        showError({
          message:
            error.message || error.title || 'Failed to print package label',
        }),
      );
      return { error };
    }
    return { data };
  };
}

function getMergedGroupPackages(
  groupID: number,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch) => {
    const { data, error } = await authGet(
      `merged-orders/${groupID}/packages/summary`,
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

function palletizeMergedOrderGroup({
  groupID,
  pallets,
}: {
  groupID: number;
  pallets: Array<{
    lengthInches: number;
    widthInches: number;
    heightInches: number;
    weightPounds: number;
    packageIDs: number[];
    packingMaterialID: number | null;
  }>;
}): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch) => {
    const { data, error } = await authPost(
      `merged-orders/${groupID}/palletize`,
      { pallets },
    );
    if (error) {
      dispatch(
        showError({
          message:
            error.message || error.title || 'Failed to palletize merge group',
        }),
      );
      return { error };
    }
    return { data };
  };
}

function expressProcessAndPrintItemLabel(
  scannedItem: string,
  payload: PrintOrderPackageLabelRequest,
): AppThunk<Promise<NormalizeReturn<PrintOrderPackageLabelResponse>>> {
  return async (dispatch, getState) => {
    const {
      systemPersist: { machineName },
    } = getState();
    const { data, error } = await dispatch(
      addResource<PrintOrderPackageLabelResponse>({
        baseUrl: `orders/item/${scannedItem}/print-item-labels`,
        payload: {
          ...payload,
          machineName: payload.printWithPrintNode ? machineName : undefined,
        },
        shouldSetUILoading: true,
        shouldShowErrorMessage: false,
      }),
    );
    if (error) {
      return {
        error,
      };
    }
    return { data };
  };
}

function getBasicMoveablePalletsForExpressProcessing(payload: {
  workflowType: ExpressProcessingWorkflowType;
  warehouseID: number;
  palletNotScannedIntoWorkstation?: boolean;
}): AppThunk<
  Promise<
    NormalizeReturn<Array<MoveablePalletForExpressProcessingBasicResponse>>
  >
> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<Array<MoveablePalletForExpressProcessingBasicResponse>>({
        baseUrl: 'orders/moveable-pallets/express-processing/basic',
        payload,
        shouldSetUILoading: true,
        shouldShowErrorMessage: false,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

function getBasicMoveablePalletOrders(
  moveablePalletIdOrRef: number | string,
): AppThunk<
  Promise<NormalizeReturn<Array<MoveablePalletOrdersBasicResponse>>>
> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<Array<MoveablePalletOrdersBasicResponse>>({
        baseUrl: `orders/moveable-pallets/${moveablePalletIdOrRef}/orders/basic`,
        payload: {},
        shouldSetUILoading: true,
        shouldShowErrorMessage: false,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

function getBasicMoveablePalletExpressItems(
  moveablePalletIdOrRef: number | string,
  payload: {
    workflowType: ExpressProcessingWorkflowType;
  },
): AppThunk<
  Promise<NormalizeReturn<Array<MoveablePalletOrderItemsBasicResponse>>>
> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<Array<MoveablePalletOrderItemsBasicResponse>>({
        baseUrl: `orders/moveable-pallets/${moveablePalletIdOrRef}/order-items/basic`,
        payload,
        shouldSetUILoading: true,
        shouldShowErrorMessage: false,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

function scanOrderItem(
  orderId: number,
  payload: GetScannedOrderItemDataRequest,
): AppThunk<Promise<NormalizeReturn<OrderItemScanResponse>>> {
  return async (dispatch, getState) => {
    const { orders: { scannedOrderItemMapping } = {} } = getState();
    const cacheKey = getOrderScanCacheKey(payload);
    const foundMapping: OrderItemScanResponse | undefined =
      scannedOrderItemMapping?.[cacheKey];
    if (foundMapping) {
      return { data: foundMapping };
    }
    const { data, error } = await dispatch(
      addResource<OrderItemScanResponse>({
        baseUrl: `orders/${orderId}/scan-item`,
        payload,
        shouldSetUILoading: false,
      }),
    );
    if (error) {
      return { error };
    }
    if (data) {
      dispatch(
        setScannedOrderItemMapping({
          scannedItem: cacheKey,
          results: data,
        }),
      );
    }
    return { data };
  };
}

function getOrderScanCacheKey(payload: GetScannedOrderItemDataRequest): string {
  return payload.unitOfMeasureType
    ? `${payload.scannedItem}:${payload.unitOfMeasureType}`
    : payload.scannedItem;
}

const processOrderActions = {
  addPhysicalItems,
  addPhysicalItemsWithInventoryTracking,
  updatePhysicalItems,
  updateAndAddPhysicalItems,
  updateAndRemovePhysicalItems,
  updateItemsRemaining,
  updateItemsCompleted,
  removeItemFromPackage,
  removeItemsRemaining,
  removeMaterialItem,
  removePhysicalItems,
  syncItemsLists,
  deletePackage,
  updatePackages,
  setHasUnsealedPackage,
  setHasUnsealedPallet,
  setPackageEditId,
  setPackages,
  setItemsRemaining,
  setItemsCompleted,
  setPhysicalItems,
  getAndUpdateItemPhysicalItems,
  scanOrderItem,
  // Inventory pool functions
  initializeInventoryPool,
  buildInventoryPool,
  rebuildInventoryPoolFromPackages,
  recordConsumedInventory,
  bulkSetOrderProcessingState,
  removeConsumedInventory,
  getRemainingRequired,
};

export {
  placeOrderOnCustomerHold,
  releaseOrderCustomerHold,
  getProcessOrder,
  processOrder,
  processFreightOrder,
  getOrderAggregatedFeesTotals,
  getOrderProcessingTimeline,
  getOrderTotalProcessingTime,
  getAndUpdateItemPhysicalItems,
  addToPhysicalItemsState,
  removePhysicalItemsFromPackage,
  printLabels,
  printPalletLabels,
  printOrderCustomsForm,
  printLabelsByExternalId,
  voidShipment,
  removeRecordedReturn,
  runOrdersImport,
  cancelOrder,
  batchCancelOrders,
  batchAutoAllocateOrders,
  mergeOrders,
  getMergedOrderGroup,
  getMergedGroupPackages,
  palletizeMergedOrderGroup,
  clearOrderPackages,
  processReship,
  processOrderActions,
  updatePackages,
  removeMaterialItem,
  addPhysicalItems,
  addPhysicalItemsWithInventoryTracking,
  updatePhysicalItems,
  updateAndAddPhysicalItems,
  updateAndRemovePhysicalItems,
  removePhysicalItems,
  updateItemsRemaining,
  updateItemsCompleted,
  removeItemFromPackage,
  removeItemsRemaining,
  syncItemsLists,
  deletePackage,
  setHasUnsealedPackage,
  pauseOrder,
  getOrderFulfillmentSchedule,
  getOrderFulfillmentProgress,
  getOrderFulfillmentExpressShipmentsProgress,
  getOrderFulfillmentOpenOrdersPerCustomer,
  printPackageLabels,
  sealPackageAndPrintLabel,
  printSinglePackageIdentificationLabel,
  expressProcessAndPrintItemLabel,
  getBasicMoveablePalletsForExpressProcessing,
  getBasicMoveablePalletOrders,
  getBasicMoveablePalletExpressItems,
  // Inventory pool functions
  initializeInventoryPool,
  buildInventoryPool,
  rebuildInventoryPoolFromPackages,
  recordConsumedInventory,
  removeConsumedInventory,
  getRemainingRequired,
};
